import React, { useEffect, useRef, useState } from 'react';
import Grid from '@mui/material/Grid';
import styled from '@emotion/styled';
import Button from '@mui/material/Button';
import Box from '@mui/material/Box';
import Autocomplete from '@mui/material/Autocomplete';
import TextField from '@mui/material/TextField';
import Link from '@mui/material/Link';
import AddIcon from '@mui/icons-material/Add';
import ClearIcon from '@mui/icons-material/Clear';
import Stack from '@mui/material/Stack';
import UploadIcon from '@mui/icons-material/Upload';
import Dialog from '@mui/material/Dialog';
import DeleteForeverIcon from '@mui/icons-material/DeleteForever';
import EditIcon from '@mui/icons-material/Edit';
import { Editor } from '@tinymce/tinymce-react';
import { Controller } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { DesktopDatePicker } from '@mui/x-date-pickers/DesktopDatePicker';
import { filter, find, get, map } from 'lodash';

import { useUploadFileMutation } from 'app/apis/upload.api';
import { useInstrument } from 'features/instrument/instrument.ctx';
import { asMarkLogicDate } from 'utils/dateUtil';

import { DocumentRowWrapper } from './document-list';

const MemberItem = styled(Grid)`
  color: #04629A;
  margin: 2px 5px;

  &:hover {
    text-decoration: underline;
    cursor: pointer;
  }
`;

const HeaderItem = styled(Grid)`
  color: #727272;
  font-weight: bold;
  font-size: 11px;
`;

const AddItemSpan = styled('span')`
  color: #04629A;

  &:hover {
    text-decoration: underline;
    cursor: pointer;
  }
`;

const AdherentsListPart = (props) => {
  const {
    data, onChange, onDelete, setUploadContent, setOpenUpload, onClick,
    setCommentContent, setOpenComment, onDeleteComment, onDeleteDocument
  } = props;
  const { instrumentListEn } = useInstrument();

  const getCountryField = () => {
    switch (data.kind) {
      case 'Member':
        return (
          <Link underline="hover" sx={{ cursor: 'pointer' }} onClick={() => onClick(data)}>
            {data.name}
          </Link>
        )
      case 'Non-member':
        return (
          <Autocomplete
            size="small"
            renderInput={params => (
              <TextField
                {...params}
                InputProps={{ ...params.InputProps, style: { fontSize: 12 } }}
              />
            )}
            options={filter(instrumentListEn.countries, e => e.kind === "Non-member")}
            getOptionLabel={option => get(option, 'name')}
            onChange={(e, nVal) => {
              onChange({ country: { id: nVal.id } });
            }}
            value={find(instrumentListEn.countries, cv => cv.id === data.country.id)}
          />
        )
      case 'International organisation':
        return (
          <Autocomplete
            size="small"
            renderInput={params => (
              <TextField
                {...params}
                InputProps={{ ...params.InputProps, style: { fontSize: 12 } }}
              />
            )}
            options={filter(instrumentListEn.countries, e => e.kind === "International organisation")}
            getOptionLabel={option => get(option, 'name')}
            onChange={(e, nVal) => {
              onChange({ country: { id: nVal.id } });
            }}
            value={find(instrumentListEn.countries, cv => cv.id === data.country.id)}
          />
        )
    }
  }

  const onEditDocument = (index) => {
    let doc = data?.documentRef[index];

    setUploadContent({
      index: data.index,
      docIndex: index,
      descriptionEn: doc.desc[0].value,
      descriptionFr: doc.desc[1].value,
      uri: doc.uri
    });
    setOpenUpload(true);
  }

  const onAddDocument = () => {
    setUploadContent({
      index: data.index, docIndex: data?.documentRef ? data.documentRef.length : 0,
      uri: "",
      descriptionEn: "",
      descriptionFr: ""
    });
    setOpenUpload(true);
  }

  const onEditComment = (lang) => {
    setCommentContent({ index: data.index, lang, value: data.comments.text[lang === 'en' ? 0 : 1].value });
    setOpenComment(true);
  }

  const onAddComment = (lang) => {
    setCommentContent({ index: data.index, lang });
    setOpenComment(true);
  }

  return (
    <Box mb={1}>
      <DocumentRowWrapper>
        <Stack direction={'row'}>
          <Grid container alignItems="center">
            <Grid item xs={3} pr={1}>
              {getCountryField()}
            </Grid>
            <Grid item xs={2} pr={1}>
              <Autocomplete
                size="small"
                renderInput={params => (
                  <TextField
                    {...params}
                    InputProps={{ ...params.InputProps, style: { fontSize: 12 } }}
                  />
                )}
                options={instrumentListEn.adherenceUpdateTypes}
                getOptionLabel={option => get(option, 'name')}
                onChange={(e, nVal) => {
                  onChange({ status: { id: nVal.id } });
                }}
                value={find(instrumentListEn.adherenceUpdateTypes, cv => cv.id === data.status.id)}
              />
            </Grid>
            <Grid item xs={2}>
              <DesktopDatePicker
                componentsProps={{
                  actionBar: {
                    actions: ['clear'],
                  },
                }}
                size="small"
                inputFormat="DD/MM/YYYY"
                inputProps={{ style: { fontSize: 12, width: 80 } }}
                value={data.date || null}
                onChange={(e) => {
                  if (e) {
                    onChange({ date: e.format('YYYY-MM-DD') })
                  } else {
                    onChange({ date: null })
                  }
                }}
                renderInput={(params) => <TextField size="small" {...params} />}
              />
            </Grid>
            <Grid item xs={1}>
              {data.comments && data.comments.text[0]?.value ? (
                <>
                  <div dangerouslySetInnerHTML={{ __html: data.comments.text[0].value }}/>
                  <EditIcon sx={{ fontSize: 14, cursor: "pointer" }} onClick={event => onEditComment('en')}/>
                  <DeleteForeverIcon sx={{ fontSize: 14, cursor: "pointer" }} onClick={event => onDeleteComment('en')}/>
                </>
              ) : (
                <AddIcon sx={{ fontSize: 14, cursor: "pointer" }} onClick={event => onAddComment('en')}/>
              )}
            </Grid>
            <Grid item xs={1}>
              {data.comments && data.comments.text[1]?.value ? (
                <>
                  <div dangerouslySetInnerHTML={{ __html: data.comments.text[1].value }}/>
                  <EditIcon sx={{ fontSize: 14, cursor: "pointer" }} onClick={event => onEditComment('fr')}/>
                  <DeleteForeverIcon sx={{ fontSize: 14, cursor: "pointer" }} onClick={event => onDeleteComment('fr')}/>
                </>
              ) : (
                <AddIcon sx={{ fontSize: 14, cursor: "pointer" }} onClick={event => onAddComment('fr')}/>
              )}
            </Grid>
            <Grid item xs={2}>
              {map(data?.documentRef, (e, i) => (
                <Box key={i}>
                  <Box sx={{ fontSize: 11 }}>
                    <a target={'_blank'} href={e.uri}>
                      {e.desc[0]?.value} - {e.desc[1]?.value}
                    </a>
                  </Box>
                  <EditIcon sx={{ fontSize: 14, cursor: "pointer" }} onClick={event => onEditDocument(i)}/>
                  <DeleteForeverIcon sx={{ fontSize: 14, cursor: "pointer" }} onClick={event => onDeleteDocument(i)}/>
                </Box>
              ))}
              <AddIcon sx={{ fontSize: 14, cursor: "pointer" }} onClick={onAddDocument}/>
            </Grid>
          </Grid>
          <ClearIcon sx={{ fontSize: 14, cursor: "pointer" }} onClick={onDelete}/>
        </Stack>
        {/*<Autocomplete/>*/}
      </DocumentRowWrapper>
    </Box>
  );
}

const AdherentsListHeader = () => {
  const { t } = useTranslation();

  return <Stack direction={'row'} style={{ borderBottom: "1px solid gray", marginRight: 20 }} mt={3} mb={1}>
    <Grid container>
      <HeaderItem item xs={3}>
        COUNTRY/TERRITORY
      </HeaderItem>
      <HeaderItem item xs={2}>
        STATUS
      </HeaderItem>
      <HeaderItem item xs={2}>
        DATE
      </HeaderItem>
      <HeaderItem item xs={1}>
        COMMENTS EN
      </HeaderItem>
      <HeaderItem item xs={1}>
        COMMENTS FR
      </HeaderItem>
      <HeaderItem item xs={2}>
        DOCUMENTS
      </HeaderItem>
      <HeaderItem item xs={1}>
        DELETE
      </HeaderItem>
    </Grid>
  </Stack>
}

const AdherentsList = ({ name, ...restProps }) => {
  const { t } = useTranslation();
  const [list, setList] = useState([]);
  const [memberSelect, setMemberSelect] = useState([]);
  const [memberList, setMemberList] = useState([]);
  const [nonMemberList, setNonMemberList] = useState([]);
  const [otherList, setOtherList] = useState([]);
  const { instrumentList } = useInstrument();

  const [openUpload, setOpenUpload] = useState(false);
  const [uploadContent, setUploadContent] = useState({});
  const uploadInputRef = useRef(null);
  const [uploadFile] = useUploadFileMutation();

  const [openComment, setOpenComment] = useState(false);
  const [commentContent, setCommentContent] = useState({ value: "", lang: "" });
  const editorRef = useRef();

  useEffect(() => {
    if (list) {
      let tmpList = map(list, (e, i) => ({
        ...instrumentList?.countries?.find(i => i.id === e.country.id),
        ...e,
        index: i
      })).sort((a, b) => a.sortKey - b.sortKey);

      setMemberSelect(filter(tmpList, e => e.kind === "Member" && !checkIfAnyFieldExists(e)));
      setMemberList(filter(tmpList, e => e.kind === "Member" && checkIfAnyFieldExists(e)));

      setNonMemberList(filter(tmpList, e => e.kind === "Non-member"));
      setOtherList(filter(tmpList, e => e.kind === "International organisation"));
    }
  }, [list]);


  const checkIfAnyFieldExists = (e) => {
    return (e.comments?.text && (e.comments.text[0]?.value || e.comments.text[1]?.value))
      || e.date
      || e.documentRef
  }

  const onChange = (index, old, changed, change) => {
    if (index > -1) {
      let tmp = [...list];
      let data = { ...tmp[index] };
      delete data.kind;
      delete data.index;
      tmp[index] = { ...data, ...changed };

      if (get(tmp, `${index}.date`) === null) {
        delete tmp[index].date;
        if (!tmp[index].documentRef) {
          tmp[index].documentRef = [];
        }
      }
      change(tmp);
    }
  }

  const onMemberItemClick = (item, change) => {
    const countryRow = find(list, x => x?.country?.id === item.id);
    const newItem = {
      ...countryRow,
      status: { id: 1 },
      date: asMarkLogicDate(new Date()),
      comments: {
        text: [{ lang: "en" }, { lang: "fr" }]
      },
      documentRef: [],
    };
    change([...list, newItem]);
  }

  const onDelete = (index, change, isMember = false) => {
    if (index > -1) {
      let tmp = [...list];
      const item = tmp[index];
      const mustBeReset = isMember && tmp.filter(i => i.country?.id === item.country?.id).length === 1;

      if (mustBeReset) {
        tmp[index] = { country: item.country, status: { id: 1 } }
      } else {
        tmp.splice(index, 1);
      }

      change(tmp);
    }
  }

  const onChangeUpload = (e) => {
    let selectedFile = e.target.files[0];
    const formData = new FormData();
    formData.append('file', selectedFile);
    formData.append('uploadType', 'file');

    uploadFile(formData)
      .then((resp) => {
        setUploadContent({
          ...uploadContent,
          descriptionEn: selectedFile.name,
          descriptionFr: selectedFile.name,
          uri: resp.data.temporaryLocation
        });
      });
  }

  const onSaveUpload = (change) => {
    let index = uploadContent.index;
    let docIndex = uploadContent.docIndex;
    if (index > -1) {
      let tmp = [...list];
      let documentRef = tmp[index].documentRef;
      if (!documentRef)
        documentRef = [];

      documentRef[docIndex] = {
        lang: 'en',
        format: 'file',
        uri: uploadContent.uri,
        desc: [
          {
            lang: "en",
            value: uploadContent.descriptionEn
          },
          {
            lang: "fr",
            value: uploadContent.descriptionFr
          }
        ],
        narrative: [{ lang: "en" }, { lang: "fr" }]
      }

      tmp[index] = { ...tmp[index], documentRef }
      change(tmp);
      setUploadContent({
        uri: "",
        descriptionEn: "",
        descriptionFr: ""
      });
    }
    setOpenUpload(false);
  }

  const onSaveComment = (change) => {
    let index = commentContent.index;
    let tmp = [...list];

    let existingComment = tmp[index].comments;
    if (!existingComment) {
      existingComment = {
        text: [
          {
            lang: "en",
            value: ""
          },
          {
            lang: "fr",
            value: ""
          }
        ]
      }
    }
    existingComment.text[commentContent.lang === 'en' ? 0 : 1].value = editorRef.current.getContent();


    tmp[index] = { ...tmp[index], comments: existingComment }
    change(tmp);
    setOpenComment(false);
  }

  const onDeleteDocument = (index, docIndex, change) => {
    let tmp = [...list];
    let documentRef = tmp[index].documentRef;
    documentRef.splice(docIndex, 1);
    tmp[index] = { ...tmp[index], documentRef }
    change(tmp);
  }

  const onDeleteComment = (index, lang, change) => {
    let tmp = [...list];
    let existingComment = tmp[index].comments;
    existingComment.text[lang === 'en' ? 0 : 1].value = "";
    tmp[index] = { ...tmp[index], comments: existingComment }
    change(tmp);
    setOpenComment(false);
  }

  return (
    <Controller
      name={name}
      render={({ field }) => {

        if (field?.value) {
          setList(field.value)
        }

        return (
          <>
            <Grid container>
              <Grid item xs={12}>
                OECD Members
                <DocumentRowWrapper>
                  <Grid container pt={1} pb={1}>
                    {map(memberSelect, (e) => (
                      <MemberItem
                        item key={e.index}
                        onClick={event => onChange(e.index, e, { date: null }, field.onChange)}
                      >
                        {e.name}
                      </MemberItem>
                    ))}
                  </Grid>
                </DocumentRowWrapper>
              </Grid>
              <Grid item xs={12} mb={2}>
                <AdherentsListHeader/>
                {map(memberList, (e) => (
                  <AdherentsListPart
                    key={e.index}
                    data={e}
                    setUploadContent={setUploadContent}
                    setOpenUpload={setOpenUpload}
                    setCommentContent={setCommentContent}
                    setOpenComment={setOpenComment}
                    onClick={data => onMemberItemClick(data, field.onChange)}
                    onDelete={event => onDelete(e.index, field.onChange, true)}
                    onDeleteComment={(lang) => onDeleteComment(e.index, lang, field.onChange)}
                    onDeleteDocument={(docIndex) => onDeleteDocument(e.index, docIndex, field.onChange)}
                    onChange={(changed) => onChange(e.index, e, changed, field.onChange)}
                  />
                ))}
              </Grid>
              <Grid item xs={12}>
                Non-Members
                <AdherentsListHeader/>
                {map(nonMemberList, (e) => (
                  <AdherentsListPart
                    key={e.index} data={e}
                    setUploadContent={setUploadContent}
                    setOpenUpload={setOpenUpload}
                    setCommentContent={setCommentContent}
                    setOpenComment={setOpenComment}
                    onDelete={event => onDelete(e.index, field.onChange)}
                    onDeleteComment={(lang) => onDeleteComment(e.index, lang, field.onChange)}
                    onDeleteDocument={(docIndex) => onDeleteDocument(e.index, docIndex, field.onChange)}
                    onChange={(changed) => onChange(e.index, e, changed, field.onChange)}
                  />
                ))}
              </Grid>
              <Grid item xs={12} mt={1} mb={2}>
                <DocumentRowWrapper>
                  <AddItemSpan
                    style={{ cursor: "pointer" }}
                    onClick={() => {
                      field.onChange([...list, {
                        status: { id: 1 },
                        index: list.length,
                        country: {},
                        kind: 'Non-member'
                      }])
                    }}><AddIcon sx={{ fontSize: 14 }}/> add new line
                  </AddItemSpan>
                </DocumentRowWrapper>
              </Grid>
              <Grid item xs={12} mt={2}>
                Others/EU
                <AdherentsListHeader/>
                {map(otherList, (e) => (
                  <AdherentsListPart
                    key={e.index} data={e}
                    setUploadContent={setUploadContent}
                    setOpenUpload={setOpenUpload}
                    setCommentContent={setCommentContent}
                    setOpenComment={setOpenComment}
                    onDelete={event => onDelete(e.index, field.onChange)}
                    onDeleteComment={(lang) => onDeleteComment(e.index, lang, field.onChange)}
                    onDeleteDocument={(docIndex) => onDeleteDocument(e.index, docIndex, field.onChange)}
                    onChange={(changed) => onChange(e.index, e, changed, field.onChange)}
                  />
                ))}
              </Grid>
              <Grid item xs={12} mt={1}>
                <DocumentRowWrapper>
                  <AddItemSpan
                    style={{ cursor: "pointer" }}
                    onClick={() => {
                      field.onChange([...list, {
                        status: { id: 1 },
                        index: list.length,
                        country: {},
                        kind: 'International organisation'
                      }])
                    }}>
                    <AddIcon sx={{ fontSize: 14 }}/> add new line
                  </AddItemSpan>
                </DocumentRowWrapper>
              </Grid>
            </Grid>
            <Dialog
              open={openUpload}
              onClose={() => setOpenUpload(false)}
            >
              <Box p={2}>
                <Stack direction={'column'} spacing={2}>
                  <TextField
                    label={'URL or ONE Reference'}
                    inputProps={{ style: { fontSize: 12 } }}
                    size="small"
                    value={uploadContent.uri}
                    onChange={(e) => setUploadContent({ ...uploadContent, uri: e.target.value })}
                  />
                  <TextField
                    label={'Description - En'}
                    inputProps={{ style: { fontSize: 12 } }}
                    size="small"
                    value={uploadContent.descriptionEn}
                    onChange={(e) => setUploadContent({ ...uploadContent, descriptionEn: e.target.value })}
                  />
                  <TextField
                    label={'Description - Fr'}
                    inputProps={{ style: { fontSize: 12 } }}
                    size="small"
                    value={uploadContent.descriptionFr}
                    onChange={(e) => setUploadContent({ ...uploadContent, descriptionFr: e.target.value })}
                  />
                </Stack>

                <Stack direction={'row'} spacing={1} mt={2}>
                  <Button variant="contained" onClick={() => uploadInputRef.current.click()}>
                    <UploadIcon size="small"/>
                  </Button>
                  <Button variant="contained" onClick={() => onSaveUpload(field.onChange)}>
                    {t('save')}
                  </Button>
                  <Button variant="contained" onClick={() => setOpenUpload(false)}>
                    {t('close')}
                  </Button>
                </Stack>
                <input
                  type='file'
                  onChange={onChangeUpload}
                  style={{ display: 'none' }}
                  accept='.doc,.docx,.pdf,.xml,.html,.htm'
                  ref={uploadInputRef}
                />
              </Box>
            </Dialog>
            <Dialog
              open={openComment}
              onClose={() => setOpenComment(false)}
              disableEnforceFocus
            >
              <Box p={2}>
                <Stack direction={'column'} spacing={2}>
                  <Box>
                    Adherence comments - {commentContent.lang}
                  </Box>
                  <Editor
                    tinymceScriptSrc={process.env.PUBLIC_URL + '/tinymce/tinymce.min.js'}
                    onInit={(evt, editor) => editorRef.current = editor}
                    initialValue={commentContent.value ? commentContent.value : ""}
                    init={{
                      height: 400,
                      width: 400,
                      menubar: true,
                      promotion: false,
                      branding: false,
                      plugins: 'lists link',
                      toolbar: 'undo redo | formatselect | ' +
                        'bold italic backcolor | alignleft aligncenter ' +
                        'alignright alignjustify | bullist numlist outdent indent | ' +
                        'removeformat | link',
                      content_style: 'body { font-family:Helvetica,Arial,sans-serif; font-size:14px }',
                    }}
                    {...restProps}
                  />
                </Stack>

                <Stack direction={'row'} spacing={1} mt={2}>
                  <Button variant="contained" onClick={() => onSaveComment(field.onChange)}>
                    {t('save')}
                  </Button>
                  <Button variant="contained" onClick={() => setOpenComment(false)}>
                    {t('close')}
                  </Button>
                </Stack>
              </Box>
            </Dialog>
          </>
        )
      }}
    />
  )
};

export default AdherentsList;

